import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { apiClient } from '@/config/api';
export const useOrganizationsStore = defineStore('organizations', () => {
    const organizations = ref([]);
    const currentOrganization = ref(null);
    const loading = ref(false);
    const error = ref(null);
    // Computed
    const activeOrganizations = computed(() => organizations.value.filter(org => org.isActive));
    const userOwnedOrganizations = computed(() => organizations.value.filter(org => org.userRole === 'OWNER'));
    const userAdminOrganizations = computed(() => organizations.value.filter(org => ['ADMIN', 'OWNER'].includes(org.userRole)));
    // Helper function to get auth headers
    const getAuthHeaders = () => {
        const token = localStorage.getItem('auth_token');
        return token ? { Authorization: `Bearer ${token}` } : {};
    };
    // Actions
    const fetchOrganizations = async () => {
        loading.value = true;
        error.value = null;
        try {
            const response = await apiClient.get('/api/organizations');
            if (response.data.success) {
                organizations.value = response.data.data;
                // Set current organization to first one if not set
                if (!currentOrganization.value && organizations.value.length > 0) {
                    currentOrganization.value = organizations.value[0];
                }
            }
            else {
                throw new Error(response.data.error || 'Failed to fetch organizations');
            }
        }
        catch (err) {
            error.value = err.response?.data?.error || err.message || 'Failed to fetch organizations';
            console.error('Error fetching organizations:', err);
            // If no organizations exist, create a default one
            if (err.response?.status === 404 || organizations.value.length === 0) {
                const defaultOrg = {
                    id: '5919d369-8884-4e99-a7d7-6d2fbd2e3660',
                    name: 'YGP Corp',
                    description: 'Default organization',
                    isActive: true,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    userRole: 'OWNER',
                    memberCount: 1,
                    campaignCount: 0,
                    employeeCount: 0,
                    templateCount: 0,
                    domainCount: 0
                };
                organizations.value = [defaultOrg];
                currentOrganization.value = defaultOrg;
            }
        }
        finally {
            loading.value = false;
        }
    };
    const fetchOrganization = async (id) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await apiClient.get(`/api/organizations/${id}`);
            if (response.data.success) {
                return response.data.data;
            }
            else {
                throw new Error(response.data.error || 'Failed to fetch organization');
            }
        }
        catch (err) {
            error.value = err.response?.data?.error || err.message || 'Failed to fetch organization';
            console.error('Error fetching organization:', err);
            throw err;
        }
        finally {
            loading.value = false;
        }
    };
    const createOrganization = async (data) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await apiClient.post('/api/organizations', data);
            if (response.data.success) {
                await fetchOrganizations(); // Refresh the list
                return response.data.data;
            }
            else {
                throw new Error(response.data.error || 'Failed to create organization');
            }
        }
        catch (err) {
            error.value = err.response?.data?.error || err.message || 'Failed to create organization';
            console.error('Error creating organization:', err);
            throw err;
        }
        finally {
            loading.value = false;
        }
    };
    const updateOrganization = async (id, data) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await apiClient.put(`/api/organizations/${id}`, data);
            if (response.data.success) {
                await fetchOrganizations(); // Refresh the list
                return response.data.data;
            }
            else {
                throw new Error(response.data.error || 'Failed to update organization');
            }
        }
        catch (err) {
            error.value = err.response?.data?.error || err.message || 'Failed to update organization';
            console.error('Error updating organization:', err);
            throw err;
        }
        finally {
            loading.value = false;
        }
    };
    const deleteOrganization = async (id) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await apiClient.delete(`/api/organizations/${id}`);
            if (response.data.success) {
                await fetchOrganizations(); // Refresh the list
                return true;
            }
            else {
                throw new Error(response.data.error || 'Failed to delete organization');
            }
        }
        catch (err) {
            error.value = err.response?.data?.error || err.message || 'Failed to delete organization';
            console.error('Error deleting organization:', err);
            throw err;
        }
        finally {
            loading.value = false;
        }
    };
    const addMember = async (organizationId, data) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await apiClient.post(`/api/organizations/${organizationId}/members`, data);
            if (response.data.success) {
                return response.data.data;
            }
            else {
                throw new Error(response.data.error || 'Failed to add member');
            }
        }
        catch (err) {
            error.value = err.response?.data?.error || err.message || 'Failed to add member';
            console.error('Error adding member:', err);
            throw err;
        }
        finally {
            loading.value = false;
        }
    };
    const removeMember = async (organizationId, memberId) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await apiClient.delete(`/api/organizations/${organizationId}/members/${memberId}`);
            if (response.data.success) {
                return true;
            }
            else {
                throw new Error(response.data.error || 'Failed to remove member');
            }
        }
        catch (err) {
            error.value = err.response?.data?.error || err.message || 'Failed to remove member';
            console.error('Error removing member:', err);
            throw err;
        }
        finally {
            loading.value = false;
        }
    };
    const updateMemberRole = async (organizationId, memberId, role) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await apiClient.put(`/api/organizations/${organizationId}/members/${memberId}`, { role });
            if (response.data.success) {
                return response.data.data;
            }
            else {
                throw new Error(response.data.error || 'Failed to update member role');
            }
        }
        catch (err) {
            error.value = err.response?.data?.error || err.message || 'Failed to update member role';
            console.error('Error updating member role:', err);
            throw err;
        }
        finally {
            loading.value = false;
        }
    };
    const setCurrentOrganization = async (org) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await apiClient.post(`/api/organizations/switch/${org.id}`, {});
            if (response.data.success) {
                currentOrganization.value = org;
                return response.data.data;
            }
            else {
                throw new Error(response.data.error || 'Failed to switch organization');
            }
        }
        catch (err) {
            error.value = err.response?.data?.error || err.message || 'Failed to switch organization';
            console.error('Error switching organization:', err);
            throw err;
        }
        finally {
            loading.value = false;
        }
    };
    const initializeCurrentOrganization = () => {
        const storedOrgId = localStorage.getItem('current_organization_id');
        if (storedOrgId && organizations.value.length > 0) {
            const org = organizations.value.find(o => o.id === storedOrgId);
            if (org) {
                currentOrganization.value = org;
            }
        }
    };
    const clearError = () => {
        error.value = null;
    };
    return {
        // State
        organizations,
        currentOrganization,
        loading,
        error,
        // Computed
        activeOrganizations,
        userOwnedOrganizations,
        userAdminOrganizations,
        // Actions
        fetchOrganizations,
        fetchOrganization,
        createOrganization,
        updateOrganization,
        deleteOrganization,
        addMember,
        removeMember,
        updateMemberRole,
        setCurrentOrganization,
        initializeCurrentOrganization,
        clearError
    };
});
